import React from "react";
import { skillsDelete } from "../../../../api/api";
import { getToolLabel } from "../../../../hooks/useAiTools";
import { useModels } from "../../../../state/api/ai/useModels";
import { useExternalSkills, useSkills } from "../../../../state/api/ai/useSkills";
import { Doc, INTERACTION_TYPE, InteractionTypeMap, Skill } from "../../../../types";
import DeleteButton from "../../buttons/DeleteButton";
import PageLoader from "../../loader/PageLoader";
import Pill from "../../pill/Pill";
import EditSkillButton from "./EditSkillsButton";
import "./SkillManagement.css";

interface Props {
  skill?: Doc<Skill>;
  isOnly: boolean;
}

function SkillCard(props: Props) {
  const {
    skill,
    isOnly
  } = props;

  const { reloadSkills } = useSkills();
  const { externalSkillsById } = useExternalSkills();
  const { modelsById } = useModels();

  if (!skill) {
    return null;
  }

  if (!skill.skill_id || !externalSkillsById) {
    return null;
  }

  const s = externalSkillsById?.[skill.skill_id];
  const skillInfo = s?.display_title ?? skill.skill_id;
  const model = modelsById?.[skill.model];

  return (
    <div className="border rounded p-3 d-flex flex-column gap-2">
      <div className="d-flex flex-column gap-1">
        <h6 className="m-0 p-0">{skill.label || skillInfo}</h6>
        <div className="d-flex flex-row flex-wrap gap-1">
          {
            skill.inTesting && <Pill>In Testing</Pill>
          }
          {isOnly && (
            <Pill>Auto Active</Pill>
          )}
          {
            skill.fileInputRequired && <Pill>File Input</Pill>
          }
          {
            !!skill.jsonSchema && <Pill>JSON</Pill>
          }
          {
            !!skill.firstMessage && <Pill>Immediate Start</Pill>
          }
          {
            !!skill.modelFileUploadOnStart && <Pill>Workbook Upload</Pill>
          }
          {
            !!model?.directToolUseOnly && <Pill>Direct Tool Use</Pill>
          }
          {
            !!skill.firstMessageTool && (
              <Pill>
                {`Forced: ${getToolLabel(skill.firstMessageTool)}`}
              </Pill>
            )
          }
        </div>
      </div>
      <div style={{ height: "1px", width: "100%", backgroundColor: "var(--gray-100)" }} />
      <div className="d-flex flex-column gap-1">
        {
          skill.label && (
            <span className="font-code font-s">{skillInfo}</span>
          )
        }
        <span className="font-code font-s">{model ? (model.label || model.externalModelId) : "Model missing"}</span>
        {
          model?.label && (
            <span className="font-code font-s">{model.externalModelId}</span>
          )
        }
      </div>
      <div style={{ height: "1px", width: "100%", backgroundColor: "var(--gray-100)" }} />
      <div className="d-flex flex-row flex-wrap gap-1">
        <EditSkillButton skill={skill} />
        <EditSkillButton skill={skill} duplicate />
        <DeleteButton
          apiCall={() => skillsDelete(skill._id)}
          afterDelete={reloadSkills}
        />
      </div>
    </div>
  )
}

export default function SkillManagement() {
  const { skills, loadingSkills } = useSkills();
  const { loadingExternalSkills } = useExternalSkills();
  const { loadingModels } = useModels();

  const mapped = React.useMemo(() => {
    const r: { [key in INTERACTION_TYPE]?: Doc<Skill>[] } = {};

    for (const s of (skills ?? [])) {
      if (!r[s.type]) {
        r[s.type] = [];
      }

      r[s.type]!.push(s);
    }

    return Object.entries(r).map(x => ({
      type: x[0] as INTERACTION_TYPE,
      skills: x[1]
    }));
  }, [skills]);

  if (loadingSkills || loadingExternalSkills || loadingModels) return <PageLoader />;

  return (
    <div className="d-flex flex-column gap-3 ps-4 pe-4 ps-sm-3 pe-sm-3 pt-4" style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
      {
        mapped?.length
          ? (
            mapped.map(x => (
              <div className="d-flex flex-column w-100 gap-2" key={x.type}>
                <h5>{InteractionTypeMap[x.type]}</h5>
                <div className="d-flex flex-column w-100 gap-2">
                  {x.skills.map(y => (
                    <SkillCard
                      isOnly={x.skills.length === 1}
                      key={y._id}
                      skill={y}
                    />
                  ))}
                </div>
              </div>
            ))
          )
          : <span>No skills</span>
      }
    </div>
  )
}